/**
 * ActivityPluginRegistry - Registers and dispatches to activity type plugins.
 *
 * Each plugin implements a standard lifecycle interface for its activity type.
 * The registry routes engine calls to the correct plugin based on activity type.
 *
 * @see GitHub Issue #852
 */

const _plugins = new Map();

export default class ActivityPluginRegistry {
    /**
     * Register a plugin for an activity type.
     * @param {string} type - Activity type ('challenge', 'guided-creation', etc.)
     * @param {object} plugin - Plugin implementing the ActivityPlugin interface
     */
    static register (type, plugin) {
        if (!type || typeof type !== 'string') {
            throw new Error('[ActivityPluginRegistry] type must be a non-empty string');
        }
        if (!plugin || typeof plugin.init !== 'function') {
            throw new Error(`[ActivityPluginRegistry] Plugin for "${type}" must implement init()`);
        }
        _plugins.set(type, plugin);
    }

    /**
     * Get the plugin for an activity type.
     * @param {string} type
     * @returns {object|null}
     */
    static get (type) {
        return _plugins.get(type) || null;
    }

    /**
     * Check if a plugin is registered for a type.
     * @param {string} type
     * @returns {boolean}
     */
    static has (type) {
        return _plugins.has(type);
    }

    /**
     * Get all registered activity types.
     * @returns {string[]}
     */
    static types () {
        return Array.from(_plugins.keys());
    }

    /**
     * Remove a plugin registration.
     * @param {string} type
     */
    static unregister (type) {
        _plugins.delete(type);
    }

    /**
     * Clear all registrations (for testing).
     */
    static clear () {
        _plugins.clear();
    }
}

/**
 * ActivityPlugin Interface (duck-typed):
 *
 * {
 *   init(activityData, stateMachine, container): void
 *     - Set up the plugin for a specific activity instance
 *
 *   onStateChange(prevState, newState, meta): void
 *     - React to state machine transitions
 *
 *   evaluate(): { success: boolean, score: object }
 *     - Evaluate current state against success criteria
 *
 *   getUI(): { overlays: object[] }
 *     - Return any additional overlay configuration for this activity type
 *
 *   destroy(): void
 *     - Clean up all resources
 * }
 */
